import { Population } from '../types/iloe';
import { Community } from '../types/community';

export class NetworkGraph {
  private adjacencyList: Map<string, string[]>;
  private populations: Map<string, Population>;
  private communities: Map<string, Community>;

  constructor(populations: Population[], communities: Community[]) {
    this.adjacencyList = new Map();
    this.populations = new Map();
    this.communities = new Map();
    this.initialize(populations, communities);
  }

  private initialize(populations: Population[], communities: Community[]) {
    populations.forEach(pop => {
      this.populations.set(pop.id, pop);
      this.adjacencyList.set(pop.id, pop.networkedToPopulations || []);
    });
    
    communities.forEach(comm => {
      this.communities.set(comm.id, comm);
    });
  }

  public getAdjacentPopulations(populationId: string): string[] {
    return this.adjacencyList.get(populationId) || [];
  }

  public isConnected(pop1Id: string, pop2Id: string): boolean {
    const neighbors = this.getAdjacentPopulations(pop1Id);
    return neighbors.includes(pop2Id);
  }

  public getDistanceBetween(startId: string, targetId: string): number {
    const path = this.findPath(startId, targetId);
    return path.length > 0 ? path.length - 1 : -1; // -1 if no path
  }

  public findPath(startId: string, targetId: string): string[] {
    if (!this.populations.has(startId) || !this.populations.has(targetId)) return [];
    if (startId === targetId) return [startId];

    const queue: string[] = [startId];
    const visited = new Set<string>();
    const predecessors = new Map<string, string>();
    
    visited.add(startId);

    while (queue.length > 0) {
      const current = queue.shift()!;

      if (current === targetId) {
        return this.reconstructPath(predecessors, targetId);
      }

      const neighbors = this.getAdjacentPopulations(current);
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          predecessors.set(neighbor, current);
          queue.push(neighbor);
        }
      }
    }

    return []; // No path found
  }

  private reconstructPath(predecessors: Map<string, string>, targetId: string): string[] {
    const path: string[] = [];
    let current: string | undefined = targetId;

    while (current) {
      path.unshift(current);
      current = predecessors.get(current);
    }

    return path;
  }

  public isValidILOEPath(path: string[], startCommunityId: string, teamOwnedCommunities: string[]): boolean {
    if (path.length === 0) return false;

    // 1. Validate Start
    const startPopId = path[0];
    const startPop = this.populations.get(startPopId);
    
    if (!startPop) return false;
    
    // The start population must belong to the specified startCommunityId
    if (startPop.communityId !== startCommunityId) return false;

    // The start community must be owned by the team
    if (!teamOwnedCommunities.includes(startCommunityId)) return false;

    // 2. Validate Continuity
    for (let i = 0; i < path.length - 1; i++) {
      const current = path[i];
      const next = path[i + 1];
      if (!this.isConnected(current, next)) {
        return false;
      }
    }

    return true;
  }
}
